import { Pagination, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useModal } from 'shared/hooks';
import { SendToStudentsModal } from 'shared/ui';

import { DropdownOfTableButtons } from './ui';
import { audioApi, iclApi, subjectsApi, taskApi, videoApi } from '../../../api';
import copySvg from '../../../assets/img/copy.svg';
import { getStudents } from '../../../Common/Database';
import { checklistTablePopupsNames } from '../../../constants/checklistTablePopupsNames';
import { tabs } from '../../../constants/checlistTableTabs';
import { tableSize } from '../../../constants/tableSize';
import { ICL, Option } from '../../../shared/types';
import { PersonOption } from '../../../shared/types/works';
import { ItemActionModal } from '../../../shared/ui/ItemActionModal';
import { getTeachersStudent } from '../../../utils/getTeachersStudent';
import { getTeachersSubjects } from '../../../utils/getTeachersSubjects';
import { LoadingError } from '../../LoadingError';
import CheckListFilters from '../CheckListFilters';
import TableTabs from '../TableTabs';

interface Props {
  mail: string;
}

const teacherTabs = [
  { tabName: 'My templates', tabKey: tabs.myTemplates },
  { tabName: 'Students Works', tabKey: tabs.studentsWorks },
  { tabName: 'Templates', tabKey: tabs.template },
  { tabName: 'Archived', tabKey: tabs.archived },
  { tabName: 'Corrections', tabKey: tabs.corrections },
  { tabName: 'Deleted Templates', tabKey: tabs.deletedTemplates },
];

export default function ChecklistTableTeacher({ mail }: Props) {
  const {
    t,
    i18n: { language: platformLang },
  } = useTranslation();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tabQuery = searchParams.get('tab');

  const [icls, setIcls] = useState<ICL[]>([]);

  const [iclsForShow, setIclsForShow] = useState<ICL[]>([]);

  const [currentIcl, setCurrentIcl] = useState<ICL | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRemovingLoading, setIsRemovingLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState('');
  const [idForManipulation, setIdForManipulation] = useState('');
  const [tab, setTab] = useState<string>(tabs.myTemplates);
  const [page, setPage] = useState(1);
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState<Option[] | null>(null);
  const [teachersSubjects, setTeachersSubjects] = useState<string[] | null>(null);
  const [click, setClick] = useState(false);

  const [subjectFilter, setSubjectFilter] = useState<Option | null>(null);
  const [studentFilter, setStudentFilter] = useState<PersonOption | null>(null);
  const [classFilter, setClassFilter] = useState<string | null>(null);

  const { isOpen, close, open } = useModal();

  const oneDayInMs = 24 * 60 * 60 * 1000;

  const oneMinuteInMs = 1 * 60 * 1000; // use for testing deleting icls

  useEffect(() => {
    if (tabQuery) {
      setTab(tabQuery);
    }
  }, [tabQuery]);

  useEffect(() => {
    // Run the check every day to remove deleted icls  for ever
    const intervalId = setInterval(iclApi.removeDeletedIcls, oneDayInMs);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    getTeachersSubjects(mail).then(subjects => {
      setTeachersSubjects(subjects);
    });
  }, []);

  useEffect(() => {
    getTeachersStudent(mail).then(students => {
      setStudents(students);
    });
  }, []);

  useEffect(() => {
    if (iclsForShow.length === 0 && page > 1) {
      setPage(page - 1);
    }
  }, [iclsForShow]);

  useEffect(() => {
    if (teachersSubjects) {
      const updateSubjects = (subjects: Option[]) => {
        const filteredSubjects = subjects.filter(subject => teachersSubjects.includes(subject.name || subject.ruName));

        setSubjects(filteredSubjects);
      };
      const unsubscribe = subjectsApi.subscribeToChanges(updateSubjects);

      return () => {
        unsubscribe();
      };
    }
  }, [teachersSubjects]);

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);

      try {
        const res = getStudents();
        res.then(resp => {
          const arrStudents = Object.values(resp);
          setStudents(arrStudents);
        });
      } catch (error) {
        console.log(error);
      }

      setIsLoading(false);
    };

    init();
  }, []);

  useEffect(() => {
    setIsLoading(true);

    if (tab === tabs.template) {
      const unsubscribe = iclApi.subscribeToTemplateICLs({
        setNewICLs: setIcls,
        subjectFilter: subjectFilter?.id,
        classFilter,
      });

      setIsLoading(false);

      return () => {
        unsubscribe();
      };
    }
    if (tab === tabs.myTemplates || tab === tabs.deletedTemplates) {
      const unsubscribe = iclApi.subscribeToTeacherICLs({
        setNewICLs: setIcls,
        teacherEmail: mail,
        subjectFilter: subjectFilter?.id,
        classFilter,
        myICLs: true,
      });
      setIsLoading(false);

      return () => {
        unsubscribe();
      };
    }
    if (tab === tabs.studentsWorks || tab === tabs.corrections) {
      const unsubscribe = iclApi.subscribeToTeacherICLs({
        setNewICLs: setIcls,
        teacherEmail: mail,
        subjectFilter: subjectFilter?.id,
        classFilter,
        studentFilter,
        studentsWorks: true,
        ...(tab === tabs.corrections
          ? {
              correction: true,
            }
          : {}),
      });
      setIsLoading(false);

      return () => {
        unsubscribe();
      };
    }
    if (tab === tabs.archived) {
      const unsubscribe = iclApi.subscribeToTeacherICLs({
        setNewICLs: setIcls,
        teacherEmail: mail,
        isActive: false,
        studentFilter,
        subjectFilter: subjectFilter?.id,
        classFilter,
      });
      setIsLoading(false);

      return () => {
        unsubscribe();
      };
    }
  }, [tab, click]);

  useEffect(() => {
    const filteredIcls =
      tab === tabs.deletedTemplates ? icls.filter(icl => icl.deleted) : icls.filter(icl => !icl.deleted);

    const totalPages = Math.ceil(filteredIcls.length / tableSize);

    if (page > totalPages && totalPages > 0) {
      setPage(totalPages);
      return;
    }

    const start = (page - 1) * tableSize;
    const end = start + tableSize;

    setIclsForShow(filteredIcls.slice(start, end));
  }, [icls, page, tab]);

  const handleNavigateToChecklistUpdate = (id: string) => {
    navigate('/checklist-update', { state: { ICLId: id } });
  };

  const handleNavigateToPreview = (id: string) => {
    navigate('/checklist-preview', { state: { checkListId: id } });
  };

  const handleDeleteICL = async (id: string) => {
    setIsRemovingLoading(true);

    try {
      await iclApi.removeICL(id);
    } catch (e) {
      console.log(e);
      toast.error(t('Something went wrong. Try again!'));
    }

    toast.success(t('Successfully removed!'));
    setIsModalOpen('');
    setIsRemovingLoading(false);
  };

  const handleOpenDeletePopup = (id: string) => {
    setIsModalOpen(checklistTablePopupsNames.delete);
    setIdForManipulation(id);
  };

  const handleArchivePopup = (icl: ICL, isActive: boolean) => {
    setCurrentIcl(icl);
    setIsModalOpen(isActive ? checklistTablePopupsNames.archive : checklistTablePopupsNames.activate);
  };

  const handleSendForConsideration = async (id: string) => {
    const targetIcl = icls.find(it => it.id === id);

    if (!targetIcl) return;

    setIsLoading(true);

    const updatedIcl = {
      ...targetIcl,
      isConsideration: true,
    };

    try {
      await iclApi.updateICL(updatedIcl);
      toast.success(t('Successfully sent!'));
    } catch (e) {
      console.error('Error updating ICL:', e);
      toast.error(t('Error while sending'));
    } finally {
      setIsLoading(false);
    }
  };

  const changeICLStatus = async () => {
    setIsRemovingLoading(true);

    try {
      currentIcl && (await iclApi.updateICL({ ...currentIcl, isActive: !currentIcl.isActive }));
    } catch (e) {
      console.log(e);
      toast.error(t('Something went wrong. Try again!'));
    }

    toast.success(currentIcl?.isActive ? t('Successfully archived!') : t('Successfully activated!'));
    setIsModalOpen('');
    setIsRemovingLoading(false);
  };

  const handlePopupActions = async () => {
    switch (isModalOpen) {
      case 'delete':
        handleDeleteICL(idForManipulation);
        break;
      case 'copyAsTeacher':
        copyToTeacher(idForManipulation);
        break;
      case 'sendToAdmin':
        handleSendForConsideration(idForManipulation);
        break;
      case checklistTablePopupsNames.activate:
      case checklistTablePopupsNames.archive:
        await changeICLStatus();
        break;
    }

    setIsModalOpen('');
  };

  const actionPopupTitle = useMemo(() => {
    switch (isModalOpen) {
      case checklistTablePopupsNames.delete:
        return 'want to delete icl';
      case checklistTablePopupsNames.copyAsTeacher:
        return 'copyAsTether';
      case checklistTablePopupsNames.sendToAdmin:
        return 'sendToAdmin';
      case checklistTablePopupsNames.archive:
        return 'archive';
      case checklistTablePopupsNames.activate:
        return 'activate';
      default:
        return '';
    }
  }, [isModalOpen]);

  const actionPopupButtonTitle = useMemo(() => {
    switch (isModalOpen) {
      case checklistTablePopupsNames.delete:
        return 'delete';
      case checklistTablePopupsNames.copyAsTeacher:
        return 'copyAsTetherButton';
      case checklistTablePopupsNames.sendToAdmin:
        return 'sendToAdminButton';
      case checklistTablePopupsNames.archive:
        return 'archiveButton';
      case checklistTablePopupsNames.activate:
        return 'activateButton';
      default:
        return '';
    }
  }, [isModalOpen]);

  const copyToTeacher = async (id: string) => {
    setIsLoading(true);
    const targetIcl = icls.find(it => it.id === id);

    if (targetIcl) {
      await iclApi.copyICLToTeacher(targetIcl, mail).then(() => {
        setIsLoading(false);
        toast.success(t('Successfully copied!'));
      });
    }
  };

  const handleOpenCopyPopup = (id: string) => {
    setIsModalOpen(checklistTablePopupsNames.copyAsTeacher);
    setIdForManipulation(id);
  };

  const renderPreviewButton = (id: string) => {
    if (tab === tabs.template) {
      return (
        <button className="btn btn-primary more" onClick={() => handleNavigateToPreview(id)}>
          {t('preview')}
        </button>
      );
    }
  };

  const renderEditButton = (id: string, isTemplate: boolean) => {
    if (isTemplate) {
      return (
        <Tooltip
          title={t('copyAsTether')}
          componentsProps={{
            popper: {
              sx: {
                ['& .MuiTooltip-arrow']: {
                  color: '#9CDFFC',
                },
                ['& .MuiTooltip-tooltip']: {
                  backgroundColor: '#9CDFFC',
                  color: '#000000',
                  fontSize: '14px',
                  padding: '14px',
                  borderRadius: '8px',
                },
              },
            },
          }}
          arrow
        >
          <button
            className="btn more small h-[40px] w-[40px] rounded-[8px] !p-[6px]"
            onClick={() => handleOpenCopyPopup(id)}
          >
            <img src={copySvg} alt="copy" />
          </button>
        </Tooltip>
      );
    } else {
      return (
        tab !== tabs.archived && (
          <button className="btn btn-primary more" onClick={() => handleNavigateToChecklistUpdate(id)}>
            {t('editing')}
          </button>
        )
      );
    }
  };

  const handleOpenSendPopup = (id: string) => {
    console.log('send to admin');
    setIsModalOpen(checklistTablePopupsNames.sendToAdmin);
    setIdForManipulation(id);
  };

  const openSendToStudentsModal = (icl: ICL) => {
    setCurrentIcl(icl);
    open();
  };

  const handleChangePage = async (newPage: number) => {
    setPage(newPage);
  };

  const getFIOFromEmail = (email: string) => {
    for (const student of students) {
      if (student.email?.replace(/\./g, '') === email) {
        return student.FIO;
      }
    }

    return email;
  };

  const activeIcls = useMemo(() => {
    return iclsForShow.filter(icl => !icl.deleted);
  }, [iclsForShow, tab]);

  const removedIcls = useMemo(() => {
    return iclsForShow.filter(icl => icl.deleted);
  }, [iclsForShow, tab]);

  const restoreDeletedIcl = async (iclId: string) => {
    try {
      await iclApi.restoreICL(iclId);
      toast.success(t('Successfully restored!'));
    } catch (error) {
      toast.error(t('Something went wrong. Try again!'));
    }
  };

  const renderDeletedIcls = () => {
    return removedIcls.map(icl => {
      const { id, theme, subject, class: studentClass, isTemplate, studentEmail } = icl;

      return (
        <TableRow key={id}>
          <TableCell sx={{ maxWidth: 200, overflow: 'hidden', position: 'sticky', left: 0, backgroundColor: 'white' }}>
            {theme}
          </TableCell>
          <TableCell sx={{ minWidth: 100 }}>{t(studentClass)}</TableCell>
          <TableCell>{platformLang === 'ua' ? subject.name : subject.ruName}</TableCell>
          {(tab === tabs.studentsWorks || tab === tabs.corrections || tab === tabs.archived) && (
            <TableCell>{getFIOFromEmail(studentEmail)}</TableCell>
          )}

          <TableCell>
            <div className="flex flex-row justify-end gap-[10px] [&>*]:!text-[16px] lg:[&>*]:!text-[18px]">
              <button className="btn btn-primary more" onClick={() => restoreDeletedIcl(id)}>
                {t('restore')}
              </button>
            </div>
          </TableCell>
        </TableRow>
      );
    });
  };

  const renderRows = () => {
    return activeIcls.map(icl => {
      const { id, theme, subject, class: studentClass, isTemplate, studentEmail } = icl;

      return (
        <TableRow key={id}>
          <TableCell sx={{ maxWidth: 200, overflow: 'hidden', position: 'sticky', left: 0, backgroundColor: 'white' }}>
            {theme}
          </TableCell>
          <TableCell sx={{ minWidth: 100 }}>{t(studentClass)}</TableCell>
          <TableCell>{platformLang === 'ua' ? subject.name : subject.ruName}</TableCell>
          {(tab === tabs.studentsWorks || tab === tabs.corrections || tab === tabs.archived) && (
            <TableCell>{getFIOFromEmail(studentEmail)}</TableCell>
          )}

          <TableCell>
            <div className="flex flex-row justify-end gap-[10px] [&>*]:!text-[16px] lg:[&>*]:!text-[18px]">
              {renderEditButton(id, isTemplate)}
              {!isTemplate && (
                <Tooltip
                  title={t('deleteCheckList')}
                  componentsProps={{
                    popper: {
                      sx: {
                        ['& .MuiTooltip-arrow']: {
                          color: '#9CDFFC',
                        },
                        ['& .MuiTooltip-tooltip']: {
                          backgroundColor: '#9CDFFC',
                          color: '#000000',
                          fontSize: '14px',
                          padding: '14px',
                          borderRadius: '8px',
                        },
                      },
                    },
                  }}
                  arrow
                >
                  <button
                    className="btn more small h-[40px] w-[40px] rounded-[8px]"
                    onClick={() => handleOpenDeletePopup(id)}
                  >
                    <span className="icon icon-delete text-red-600" />
                  </button>
                </Tooltip>
              )}

              {renderPreviewButton(id)}

              {tab !== tabs.template && (
                <DropdownOfTableButtons
                  icl={icl}
                  tab={tab}
                  isLoading={isLoading}
                  handleArchivePopup={handleArchivePopup}
                  handleOpenSendPopup={handleOpenSendPopup}
                  openSendToStudentsModal={openSendToStudentsModal}
                />
              )}
            </div>
          </TableCell>
        </TableRow>
      );
    });
  };

  const handleApplyFilters = () => {
    setClick(!click);
  };

  const handleResetFilters = () => {
    setClick(!click);
    setStudentFilter(null);
    setSubjectFilter(null);
    setClassFilter(null);
  };

  const handleChangeTab = (tab: string) => {
    setTab(tab);
    setPage(1);
    handleResetFilters();
  };

  const hasRows = (tab === tabs.deletedTemplates ? removedIcls : activeIcls).length > 0;

  const getEmptyMessage = () => {
    switch (tab) {
      case tabs.myTemplates:
        return t('noMyChecklistTemplates');

      case tabs.studentsWorks:
        return t('noStudentChecklists');

      case tabs.template:
        return t('noChecklistTemplates');

      case tabs.archived:
        return t('noArchivedChecklists');

      case tabs.corrections:
        return t('noChecklistCorrections');

      case tabs.deletedTemplates:
        return t('noDeletedChecklistTemplates');

      default:
        return t('noChecklists');
    }
  };

  return (
    <LoadingError isLoading={isLoading && !icls}>
      <div className="lg:px-[15px]">
        <div className="mb-[20px] flex gap-[8px] lg:block">
          <TableTabs setTab={handleChangeTab} tab={tab} configArr={teacherTabs} />

          {students && subjects && (
            <CheckListFilters
              subject={subjectFilter}
              student={studentFilter}
              studentClass={classFilter}
              setClassFilter={setClassFilter}
              setStudentFilter={setStudentFilter}
              setSubjectFilter={setSubjectFilter}
              students={students}
              handleApply={handleApplyFilters}
              handleReset={handleResetFilters}
              click={click}
              subjects={subjects}
              tab={tab}
            />
          )}
        </div>

        <div className="relative mb-[20px] rounded-[24px] bg-white [box-shadow:10px_10px_0px_0px_#EBEAE7] lg:px-[20px] lg:py-[15px]">
          <TableContainer sx={{ borderRadius: '24px' }}>
            <Table aria-label="simple table">
              {hasRows && (
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        maxWidth: 200,
                        position: 'sticky',
                        left: 0,
                        backgroundColor: 'white',
                        borderTopLeftRadius: '24px',
                      }}
                    >
                      {t('theme')}
                    </TableCell>
                    <TableCell sx={{ minWidth: 100 }}>{t('class')}</TableCell>
                    <TableCell>{t('subject')}</TableCell>
                    {(tab === tabs.studentsWorks || tab === tabs.corrections || tab === tabs.archived) && (
                      <TableCell>{t('alreadySend')}</TableCell>
                    )}
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
              )}
              <TableBody>
                {!hasRows && !isLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      {getEmptyMessage()}
                    </TableCell>
                  </TableRow>
                ) : tab === tabs.deletedTemplates ? (
                  renderDeletedIcls()
                ) : (
                  renderRows()
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>

        {hasRows && (
          <Stack alignItems="center">
            <Pagination
              sx={{
                '& .MuiPaginationItem-page.Mui-selected': {
                  backgroundColor: 'white',
                  border: '1px solid #52C7FA',
                },
                '& .MuiPaginationItem-page': {
                  color: '#656D7B',
                },
              }}
              shape={'rounded'}
              page={page}
              onChange={(_, page) => handleChangePage(page)}
              count={Math.ceil(
                (tab === tabs.deletedTemplates ? icls.filter(icl => icl.deleted) : icls.filter(icl => !icl.deleted))
                  .length / tableSize,
              )}
            />
          </Stack>
        )}

        <ItemActionModal
          isOpen={Boolean(isModalOpen)}
          close={() => setIsModalOpen('')}
          title={actionPopupTitle}
          onHandle={() => handlePopupActions()}
          isSubmitButtonDisabled={isRemovingLoading}
          buttonTitle={actionPopupButtonTitle}
        />
        <SendToStudentsModal currentICL={currentIcl} isOpen={isOpen} onClose={close} />
      </div>
    </LoadingError>
  );
}
